import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { usePin } from "../context/PinContext";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { fileToBase64 } from "../lib/format";
import { Trash2, Upload, Plus, Lock, Fingerprint, Download, DatabaseBackup, ShieldOff, ShieldCheck, Pencil, KeyRound } from "lucide-react";
import { API_BASE } from "../lib/api";
import EmployeeManagement from "../components/EmployeeManagement";
import { StorageHealthCard } from "../components/StorageHealthCard";

function BackupCard() {
    const [busy, setBusy] = useState(false);
    const [restorePassword, setRestorePassword] = useState("");
    const [restoreFile, setRestoreFile] = useState(null);
    const [restoreMode, setRestoreMode] = useState("merge");

    async function doExport() {
        setBusy(true);
        try {
            const token = localStorage.getItem("wmf_token");
            const res = await fetch(`${API_BASE}/backup/export`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Export failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `lokdhara-backup-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Backup downloaded");
        } catch (e) {
            toast.error(e.message || "Export failed");
        } finally {
            setBusy(false);
        }
    }

    async function doRestore() {
        if (!restoreFile) return toast.error("Choose a backup file");
        if (!restorePassword) return toast.error("Enter admin password");
        setBusy(true);
        try {
            const text = await restoreFile.text();
            const parsed = JSON.parse(text);
            const data = parsed.collections || parsed;
            const { data: res } = await api.post("/backup/restore", {
                password: restorePassword,
                data,
                mode: restoreMode,
            });
            toast.success(`Restored: ${Object.entries(res.restored).map(([k, v]) => `${k}=${v}`).join(", ")}`);
            setRestorePassword("");
            setRestoreFile(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Restore failed");
        } finally {
            setBusy(false);
        }
    }

    return (
        <Card className="rounded-2xl border-slate-100 p-5 sm:p-6 space-y-5">
            <div>
                <div className="flex items-center gap-2 text-blue-900">
                    <DatabaseBackup className="h-4 w-4" />
                    <h3 className="font-display font-bold text-slate-900">Backup & Restore</h3>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                    Download a full encrypted JSON of all your data, or restore from a previous backup. Always store backups securely.
                </p>
            </div>

            <div className="space-y-2">
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                    Export
                </div>
                <Button
                    onClick={doExport}
                    disabled={busy}
                    data-testid="backup-export-btn"
                    className="h-11 rounded-xl bg-blue-900 hover:bg-blue-800"
                >
                    <Download className="h-4 w-4 mr-1.5" />
                    Download Full Backup (JSON)
                </Button>
            </div>

            <div className="space-y-3 border-t border-slate-100 pt-5">
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                    Restore
                </div>
                <Card className="rounded-xl bg-amber-50 border-amber-100 p-3 text-xs text-amber-900">
                    Restoring will <b>merge</b> records by id, or <b>replace</b> entire collections if Replace mode is selected. Always export a backup first.
                </Card>
                <div>
                    <Label>Backup file</Label>
                    <input
                        type="file"
                        accept="application/json"
                        data-testid="backup-restore-file"
                        onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                        className="block text-sm mt-1.5"
                    />
                </div>
                <div>
                    <Label>Restore mode</Label>
                    <Select value={restoreMode} onValueChange={setRestoreMode}>
                        <SelectTrigger className="h-11 rounded-xl max-w-xs" data-testid="restore-mode-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="merge">Merge (upsert by id)</SelectItem>
                            <SelectItem value="replace">Replace (wipe & restore)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>Confirm with your admin password</Label>
                    <Input
                        type="password"
                        data-testid="restore-password-input"
                        value={restorePassword}
                        onChange={(e) => setRestorePassword(e.target.value)}
                        className="h-11 rounded-xl max-w-xs mt-1.5"
                    />
                </div>
                <Button
                    onClick={doRestore}
                    disabled={busy}
                    data-testid="backup-restore-btn"
                    className="h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white"
                >
                    <Upload className="h-4 w-4 mr-1.5" />
                    Restore from Backup
                </Button>
            </div>
        </Card>
    );
}

function PinSettingsCard() {
    const pin = usePin();
    const [newPin, setNewPin] = useState("");
    const [confirmPin, setConfirmPin] = useState("");
    const [disablePin, setDisablePin] = useState("");

    async function save() {
        if (newPin.length < 4) return toast.error("PIN must be 4-8 digits");
        if (newPin !== confirmPin) return toast.error("PINs do not match");
        await pin.setPin(newPin);
        setNewPin("");
        setConfirmPin("");
        toast.success("PIN set");
    }
    async function disable() {
        const ok = await pin.disablePin(disablePin);
        if (ok) {
            setDisablePin("");
            toast.success("PIN disabled");
        } else {
            toast.error("Wrong PIN");
        }
    }

    return (
        <Card className="rounded-2xl border-slate-100 p-5 sm:p-6 space-y-5">
            <div>
                <div className="flex items-center gap-2 text-blue-900">
                    <Lock className="h-4 w-4" />
                    <h3 className="font-display font-bold text-slate-900">App PIN Lock</h3>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                    Adds a 4–8 digit PIN gate to your app. Auto-locks after 5 minutes of inactivity. Use fingerprint or Face ID via your device's built-in security (placeholder for biometric prompt).
                </p>
            </div>

            {pin.enabled ? (
                <div className="space-y-3">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100 px-2.5 py-1 rounded-full">
                        <Lock className="h-3 w-3" /> PIN is enabled
                    </div>
                    <div>
                        <Label>Enter current PIN to disable</Label>
                        <div className="flex gap-2 mt-1.5">
                            <Input
                                type="password"
                                inputMode="numeric"
                                maxLength={8}
                                data-testid="disable-pin-input"
                                value={disablePin}
                                onChange={(e) => setDisablePin(e.target.value.replace(/\D/g, ""))}
                                className="h-11 rounded-xl max-w-xs"
                            />
                            <Button
                                onClick={disable}
                                variant="outline"
                                className="h-11 rounded-xl"
                                data-testid="disable-pin-btn"
                            >
                                Disable PIN
                            </Button>
                        </div>
                    </div>
                    <Button
                        onClick={pin.lockNow}
                        variant="outline"
                        className="h-11 rounded-xl"
                        data-testid="lock-now-btn"
                    >
                        Lock app now
                    </Button>
                    <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div>
                                <h4 className="font-semibold text-slate-900 text-sm">
                                    Biometric / Device unlock
                                </h4>
                                <p className="text-[11px] text-slate-500 mt-0.5">
                                    {pin.biomAvailable
                                        ? "Use your device fingerprint / Face ID / device PIN to unlock this app."
                                        : "Your browser/device does not support biometric. Use App PIN instead."}
                                </p>
                            </div>
                            {pin.biomAvailable && !pin.biomEnabled && (
                                <Button
                                    onClick={async () => {
                                        try {
                                            await pin.enableBiometric();
                                            toast.success("Biometric enabled");
                                        } catch (e) {
                                            toast.error(e?.message || "Enrolment failed");
                                        }
                                    }}
                                    data-testid="enable-biometric-btn"
                                    className="h-10 rounded-xl bg-blue-900 hover:bg-blue-800"
                                >
                                    Enable biometric
                                </Button>
                            )}
                            {pin.biomEnabled && (
                                <Button
                                    onClick={() => {
                                        pin.disableBiometric();
                                        toast.success("Biometric disabled");
                                    }}
                                    variant="outline"
                                    data-testid="disable-biometric-btn"
                                    className="h-10 rounded-xl"
                                >
                                    Disable biometric
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="space-y-3 max-w-md">
                    <div>
                        <Label>Set new PIN (4–8 digits)</Label>
                        <Input
                            type="password"
                            inputMode="numeric"
                            maxLength={8}
                            data-testid="new-pin-input"
                            value={newPin}
                            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                            className="h-11 rounded-xl mt-1.5"
                        />
                    </div>
                    <div>
                        <Label>Confirm PIN</Label>
                        <Input
                            type="password"
                            inputMode="numeric"
                            maxLength={8}
                            data-testid="confirm-pin-input"
                            value={confirmPin}
                            onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
                            className="h-11 rounded-xl mt-1.5"
                        />
                    </div>
                    <Button
                        onClick={save}
                        data-testid="save-pin-btn"
                        className="h-11 rounded-xl bg-blue-900 hover:bg-blue-800"
                    >
                        Enable PIN Lock
                    </Button>
                </div>
            )}

            <div className="border-t border-slate-100 pt-4">
                <div className="flex items-center gap-2 text-slate-700">
                    <Fingerprint className="h-4 w-4" />
                    <span className="text-sm font-semibold">Biometric & OTP Reset</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                    Fingerprint / Face ID uses your device's built-in security. SMS / Email OTP reset is coming soon — for now, if you forget your PIN you can sign out and sign back in with your username & password to clear it.
                </p>
            </div>
        </Card>
    );
}

function UserManagementCard({ user, users, onChanged }) {
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";
    const [unCurrent, setUnCurrent] = useState("");
    const [unNew, setUnNew] = useState("");
    const [pwCurrent, setPwCurrent] = useState("");
    const [pwNew, setPwNew] = useState("");
    const [pwConfirm, setPwConfirm] = useState("");
    const [resetFor, setResetFor] = useState(null);
    const [resetPw, setResetPw] = useState("");
    const [busy, setBusy] = useState(false);

    async function changeUsername() {
        if (!unCurrent || !unNew) return toast.error("Both fields required");
        setBusy(true);
        try {
            await api.post("/users/me/change-username", {
                current_password: unCurrent,
                new_username: unNew,
            });
            toast.success("Username updated. Please log in again with the new username.");
            setUnCurrent("");
            setUnNew("");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function changePassword() {
        if (!pwCurrent || !pwNew) return toast.error("All fields required");
        if (pwNew !== pwConfirm) return toast.error("New passwords don't match");
        if (pwNew.length < 6) return toast.error("Password must be at least 6 characters");
        setBusy(true);
        try {
            await api.post("/users/me/change-password", {
                current_password: pwCurrent,
                new_password: pwNew,
            });
            toast.success("Password updated");
            setPwCurrent("");
            setPwNew("");
            setPwConfirm("");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function resetEmployeePassword() {
        if (!resetPw || resetPw.length < 6) return toast.error("Min 6 chars");
        setBusy(true);
        try {
            const { data } = await api.post(`/users/${resetFor.id}/reset-password`, {
                new_password: resetPw,
            });
            toast.success(`Password reset for ${data.username}`);
            setResetFor(null);
            setResetPw("");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="space-y-5">
            <Card className="rounded-2xl border-slate-100 p-5 sm:p-6 space-y-4" data-testid="my-account-card">
                <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                        Signed in as
                    </div>
                    <div className="font-display font-bold text-slate-900 text-lg">
                        {user?.name} <span className="text-slate-500 text-sm font-normal">@{user?.username}</span>
                    </div>
                </div>

                <div className="border-t border-slate-100 pt-4 grid sm:grid-cols-2 gap-5">
                    <div className="space-y-2.5">
                        <h4 className="font-semibold text-slate-800">Change Username</h4>
                        <Input
                            type="password"
                            placeholder="Current password"
                            data-testid="un-current-pw"
                            value={unCurrent}
                            onChange={(e) => setUnCurrent(e.target.value)}
                            className="h-10 rounded-xl"
                        />
                        <Input
                            placeholder="New username"
                            data-testid="un-new"
                            value={unNew}
                            onChange={(e) => setUnNew(e.target.value)}
                            className="h-10 rounded-xl"
                        />
                        <Button
                            onClick={changeUsername}
                            disabled={busy}
                            data-testid="un-save-btn"
                            className="h-10 rounded-xl bg-blue-900 hover:bg-blue-800"
                        >
                            Update Username
                        </Button>
                    </div>

                    <div className="space-y-2.5">
                        <h4 className="font-semibold text-slate-800">Change Password</h4>
                        <Input
                            type="password"
                            placeholder="Current password"
                            data-testid="pw-current"
                            value={pwCurrent}
                            onChange={(e) => setPwCurrent(e.target.value)}
                            className="h-10 rounded-xl"
                        />
                        <Input
                            type="password"
                            placeholder="New password (min 6 chars)"
                            data-testid="pw-new"
                            value={pwNew}
                            onChange={(e) => setPwNew(e.target.value)}
                            className="h-10 rounded-xl"
                        />
                        <Input
                            type="password"
                            placeholder="Confirm new password"
                            data-testid="pw-confirm"
                            value={pwConfirm}
                            onChange={(e) => setPwConfirm(e.target.value)}
                            className="h-10 rounded-xl"
                        />
                        <Button
                            onClick={changePassword}
                            disabled={busy}
                            data-testid="pw-save-btn"
                            className="h-10 rounded-xl bg-blue-900 hover:bg-blue-800"
                        >
                            Update Password
                        </Button>
                    </div>
                </div>
            </Card>

            {isAdmin && (
                <Card className="rounded-2xl border-slate-100 p-5 sm:p-6" data-testid="reset-employee-card">
                    <h3 className="font-display font-bold text-slate-900 mb-3">
                        Reset Employee Password
                    </h3>
                    <p className="text-xs text-slate-500 mb-4">
                        Use this if an employee forgets their password. They will need to log in
                        with the new password.
                    </p>
                    <div className="divide-y divide-slate-100">
                        {users.filter((u) => u.id !== user.id).map((u) => (
                            <div
                                key={u.id}
                                className="flex items-center justify-between py-3"
                                data-testid={`reset-row-${u.id}`}
                            >
                                <div>
                                    <div className="font-semibold text-slate-900">{u.name}</div>
                                    <div className="text-xs text-slate-500">@{u.username} · {u.role}</div>
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setResetFor(u)}
                                    data-testid={`reset-btn-${u.id}`}
                                    className="rounded-lg"
                                >
                                    Reset Password
                                </Button>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader>
                        <DialogTitle>Reset password for {resetFor?.name}</DialogTitle>
                    </DialogHeader>
                    <div>
                        <Label>New password (min 6 chars)</Label>
                        <Input
                            type="password"
                            data-testid="reset-pw-input"
                            value={resetPw}
                            onChange={(e) => setResetPw(e.target.value)}
                            className="h-11 rounded-xl mt-1.5"
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={resetEmployeePassword}
                            disabled={busy}
                            data-testid="reset-pw-confirm"
                            className="bg-rose-600 hover:bg-rose-700"
                        >
                            Confirm Reset
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function Settings() {
    const { t, lang, setLang } = useLang();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";
    const [settings, setSettings] = useState({});
    const [users, setUsers] = useState([]);
    const [newUser, setNewUser] = useState({
        username: "",
        password: "",
        name: "",
        role: "employee",
    });

    async function load() {
        const s = await api.get("/settings");
        setSettings(s.data);
        if (isAdmin) {
            const u = await api.get("/users");
            setUsers(u.data);
        }
    }
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, []);

    async function saveSettings() {
        try {
            await api.put("/settings", {
                company_name: settings.company_name || "",
                company_logo: settings.company_logo || "",
                bank_qr: settings.bank_qr || "",
                upi_id: settings.upi_id || "",
                address: settings.address || "",
                phone: settings.phone || "",
            });
            toast.success("Settings saved");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function createEmployee() {
        if (!newUser.username || !newUser.password)
            return toast.error("Username and password required");
        try {
            await api.post("/users", newUser);
            toast.success("Employee created");
            setNewUser({ username: "", password: "", name: "", role: "employee" });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function delUser(id) {
        try {
            await api.delete(`/users/${id}`);
            toast.success("Removed");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <div className="space-y-5">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
                {t("settings")}
            </h1>

            <Tabs defaultValue={isAdmin ? "branding" : "pin"}>
                <TabsList className="bg-white border border-slate-100 rounded-xl">
                    {isAdmin && (
                        <TabsTrigger value="branding" data-testid="tab-branding">
                            {t("company_branding")}
                        </TabsTrigger>
                    )}
                    {isAdmin && (
                        <TabsTrigger value="qr" data-testid="tab-qr">
                            {t("bank_qr")}
                        </TabsTrigger>
                    )}
                    {isAdmin && (
                        <TabsTrigger value="employees" data-testid="tab-employees">
                            {t("employees")}
                        </TabsTrigger>
                    )}
                    <TabsTrigger value="appearance" data-testid="tab-appearance">
                        Appearance
                    </TabsTrigger>
                    <TabsTrigger value="pin" data-testid="tab-pin">
                        App Lock
                    </TabsTrigger>
                    <TabsTrigger value="account" data-testid="tab-account">
                        Account
                    </TabsTrigger>
                    {isAdmin && (
                        <TabsTrigger value="backup" data-testid="tab-backup">
                            Backup
                        </TabsTrigger>
                    )}
                </TabsList>

                <TabsContent value="branding" className="mt-4">
                    <Card className="rounded-2xl border-slate-100 p-5 sm:p-6 space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label>{t("company_name")}</Label>
                                <Input
                                    data-testid="company-name-input"
                                    value={settings.company_name || ""}
                                    onChange={(e) =>
                                        setSettings({ ...settings, company_name: e.target.value })
                                    }
                                    className="h-11 rounded-xl"
                                    disabled={!isAdmin}
                                />
                            </div>
                            <div>
                                <Label>Phone</Label>
                                <Input
                                    value={settings.phone || ""}
                                    onChange={(e) =>
                                        setSettings({ ...settings, phone: e.target.value })
                                    }
                                    className="h-11 rounded-xl"
                                    disabled={!isAdmin}
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Address</Label>
                                <Input
                                    value={settings.address || ""}
                                    onChange={(e) =>
                                        setSettings({ ...settings, address: e.target.value })
                                    }
                                    className="h-11 rounded-xl"
                                    disabled={!isAdmin}
                                />
                            </div>
                        </div>

                        <div>
                            <Label>{t("company_logo")}</Label>
                            <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                                {settings.company_logo ? (
                                    <img
                                        src={settings.company_logo}
                                        alt="logo"
                                        className="h-14 w-14 rounded-xl object-cover border border-slate-200"
                                    />
                                ) : (
                                    <div className="h-14 w-14 rounded-xl border border-dashed border-slate-200 grid place-items-center text-slate-400 text-[10px]">
                                        No logo
                                    </div>
                                )}
                                {isAdmin && (
                                    <label
                                        data-testid="logo-upload"
                                        className="cursor-pointer inline-flex items-center gap-2 px-3 h-11 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-medium"
                                    >
                                        <Upload className="h-4 w-4" />
                                        {settings.company_logo ? "Replace Logo" : "Upload Logo"}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={async (e) => {
                                                const f = e.target.files?.[0];
                                                if (!f) return;
                                                const b = await fileToBase64(f);
                                                setSettings({ ...settings, company_logo: b });
                                            }}
                                        />
                                    </label>
                                )}
                                {isAdmin && settings.company_logo && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        data-testid="logo-delete-btn"
                                        className="h-11 rounded-xl text-rose-700 border-rose-200 hover:bg-rose-50"
                                        onClick={() =>
                                            setSettings({ ...settings, company_logo: "" })
                                        }
                                    >
                                        Delete Logo
                                    </Button>
                                )}
                            </div>
                        </div>

                        {isAdmin && (
                            <Button
                                onClick={saveSettings}
                                data-testid="save-settings-btn"
                                className="h-11 rounded-xl bg-blue-900 hover:bg-blue-800 tap"
                            >
                                {t("save")}
                            </Button>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="qr" className="mt-4">
                    <Card className="rounded-2xl border-slate-100 p-5 sm:p-6 space-y-4">
                        <div>
                            <Label>{t("upi_id")}</Label>
                            <Input
                                data-testid="upi-id-input"
                                value={settings.upi_id || ""}
                                onChange={(e) =>
                                    setSettings({ ...settings, upi_id: e.target.value })
                                }
                                className="h-11 rounded-xl max-w-md"
                                placeholder="company@upi"
                                disabled={!isAdmin}
                            />
                        </div>
                        <div>
                            <Label>{t("bank_qr")}</Label>
                            <div className="mt-2 flex items-start gap-4 flex-wrap">
                                {settings.bank_qr ? (
                                    <img
                                        src={settings.bank_qr}
                                        alt="QR"
                                        className="h-44 w-44 rounded-2xl object-cover border border-slate-200"
                                    />
                                ) : (
                                    <div className="h-44 w-44 rounded-2xl border border-dashed border-slate-200 grid place-items-center text-slate-400 text-xs">
                                        No QR uploaded
                                    </div>
                                )}
                                {isAdmin && (
                                    <label
                                        data-testid="qr-upload"
                                        className="cursor-pointer inline-flex items-center gap-2 px-3 h-11 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-medium"
                                    >
                                        <Upload className="h-4 w-4" />
                                        {settings.bank_qr ? "Replace QR" : "Upload QR"}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={async (e) => {
                                                const f = e.target.files?.[0];
                                                if (!f) return;
                                                const b = await fileToBase64(f);
                                                setSettings({ ...settings, bank_qr: b });
                                            }}
                                        />
                                    </label>
                                )}
                                {isAdmin && settings.bank_qr && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        data-testid="qr-delete-btn"
                                        className="h-11 rounded-xl text-rose-700 border-rose-200 hover:bg-rose-50"
                                        onClick={() =>
                                            setSettings({ ...settings, bank_qr: "" })
                                        }
                                    >
                                        Delete QR
                                    </Button>
                                )}
                            </div>
                        </div>
                        {isAdmin && (
                            <Button
                                onClick={saveSettings}
                                data-testid="save-qr-btn"
                                className="h-11 rounded-xl bg-blue-900 hover:bg-blue-800 tap"
                            >
                                {t("save")}
                            </Button>
                        )}
                    </Card>
                </TabsContent>

                {isAdmin && (
                    <TabsContent value="employees" className="mt-4 space-y-4">
                        <EmployeeManagement currentUser={user} onChanged={load} />
                    </TabsContent>
                )}

                <TabsContent value="appearance" className="mt-4">
                    <Card className="rounded-2xl border-slate-100 p-5 sm:p-6 space-y-6">
                        <div>
                            <Label className="text-sm font-semibold">Language</Label>
                            <p className="text-xs text-slate-500 mb-2">
                                Choose the language for menus, labels and currency display.
                            </p>
                            <div className="flex gap-2 flex-wrap">
                                {[
                                    { v: "en", label: "English" },
                                    { v: "mr", label: "मराठी (Marathi)" },
                                ].map((opt) => (
                                    <button
                                        key={opt.v}
                                        type="button"
                                        data-testid={`lang-opt-${opt.v}`}
                                        onClick={() => setLang(opt.v)}
                                        aria-pressed={lang === opt.v}
                                        className={`px-4 h-11 rounded-xl text-sm font-semibold border tap transition ${
                                            lang === opt.v
                                                ? "bg-blue-900 text-white border-blue-900"
                                                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="pin" className="mt-4">
                    <PinSettingsCard />
                </TabsContent>

                <TabsContent value="account" className="mt-4">
                    <UserManagementCard user={user} users={users} onChanged={load} />
                </TabsContent>

                {isAdmin && (
                    <TabsContent value="backup" className="mt-4 space-y-4">
                        <StorageHealthCard />
                        <BackupCard />
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}
